import { Pencil, Plus, Sliders, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import {
  createPricingRule,
  deletePricingRule,
  getDishById,
  getDishesBySite,
  getPricingRules,
  getPricingSignals,
  updatePricingRule,
  updatePricingSignals,
} from './pricingApi';

const PRICING_MODES = [
  { value: 'manual', label: 'Manual' },
  { value: 'auto', label: 'Auto' },
];

const ROUNDING_RULES = [
  { value: '', label: 'None' },
  { value: 'nearest_5', label: 'Nearest ₹5' },
  { value: 'nearest_10', label: 'Nearest ₹10' },
];

const emptyRule = () => ({
  dish_id: '',
  site_id: '*',
  base_price: '',
  min_floor_price: '',
  max_surge_price: '',
  spread_limit_percent: '',
  pricing_mode: 'auto',
  is_active: true,
  target_margin_percent: '',
  price_rounding_rule: '',
});

const PricingRulesScreen = () => {
  // Log screen view
  useScreenLogging('PricingRulesScreen', {
    component: 'PricingRulesScreen',
    screenType: 'pricing-rules',
  });

  const { openOverlay } = useOverlay();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyRule());
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [signals, setSignals] = useState({ competitor_price_index: '', event_weather_index: '' });
  const [signalsLoading, setSignalsLoading] = useState(true);
  const [signalsSaving, setSignalsSaving] = useState(false);
  const [signalsMessage, setSignalsMessage] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [dishesLoading, setDishesLoading] = useState(false);
  const [dishesError, setDishesError] = useState(null);
  const [dishNames, setDishNames] = useState({});

  const loadSites = useCallback(async () => {
    try {
      const response = await getAllSites();
      const list = Array.isArray(response?.sites) ? response.sites : Array.isArray(response) ? response : [];
      const sorted = [...list].sort((a, b) => (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || ''));
      setSites(sorted);
      // Keep "All Sites" selected by default - don't auto-select first site
    } catch (e) {
      console.error('Pricing Rules: fetch sites error', e);
      setSites([]);
      // Keep "All Sites" selected by default - don't auto-select first site
    }
  }, []);

  const loadDishNames = useCallback(async (dishIds) => {
    if (!dishIds || dishIds.length === 0) return;
    const uniqueIds = [...new Set(dishIds.filter(Boolean))];
    
    // Check current state to find missing IDs
    setDishNames((currentNames) => {
      const missingIds = uniqueIds.filter((id) => !currentNames[id]);
      if (missingIds.length === 0) return currentNames;

      // Fetch all dishes in parallel
      const promises = missingIds.map((dishId) => getDishById(dishId));
      Promise.allSettled(promises).then((results) => {
        setDishNames((prevNames) => {
          const updatedNames = { ...prevNames };
          results.forEach((result, index) => {
            const dishId = missingIds[index];
            if (result.status === 'fulfilled' && result.value.success && result.value.data) {
              updatedNames[dishId] = result.value.data.name || dishId;
            } else {
              // Keep dish_id as fallback if fetch fails
              updatedNames[dishId] = dishId;
            }
          });
          return updatedNames;
        });
      });
      return currentNames;
    });
  }, []);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    setRulesError(null);
    try {
      const result = await getPricingRules({ site_id: selectedSite || undefined });
      const rulesList = Array.isArray(result.data) ? result.data : result.data ? [result.data] : [];
      if (result.success && result.data != null) {
        setRules(rulesList);
        // Load dish names for all unique dish_ids in the rules
        const dishIds = rulesList.map((r) => r.dish_id).filter(Boolean);
        if (dishIds.length > 0) {
          loadDishNames(dishIds);
        }
      } else {
        setRules(rulesList);
        if (!result.success) setRulesError(result.message || 'Failed to load rules');
        // Still try to load dish names even if rules load failed
        const dishIds = rulesList.map((r) => r.dish_id).filter(Boolean);
        if (dishIds.length > 0) {
          loadDishNames(dishIds);
        }
      }
    } catch (err) {
      setRulesError(err?.message || 'Failed to load rules');
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [selectedSite, loadDishNames]);

  const loadSignals = useCallback(async () => {
    setSignalsLoading(true);
    try {
      const result = await getPricingSignals({ site_id: selectedSite || undefined });
      if (result.success && result.data) {
        const d = result.data;
        setSignals({
          competitor_price_index: d.competitor_price_index != null ? String(d.competitor_price_index) : '',
          event_weather_index: d.event_weather_index != null ? String(d.event_weather_index) : '',
        });
      }
    } catch (e) {
      console.error('Pricing Rules: load signals error', e);
    } finally {
      setSignalsLoading(false);
    }
  }, [selectedSite]);

  const loadDishes = useCallback(async () => {
    const effectiveSiteId = form.site_id === '*' ? (selectedSite || sites[0]?.site_id || sites[0]?.id) : form.site_id;
    if (!effectiveSiteId) {
      setDishes([]);
      setDishesError(null);
      return;
    }
    setDishesLoading(true);
    setDishesError(null);
    try {
      const result = await getDishesBySite(effectiveSiteId);
      if (result.success && result.data != null) {
        const list = Array.isArray(result.data) ? result.data : result.data?.dishes ?? [];
        setDishes(list);
        setDishesError(null);
      } else {
        setDishes([]);
        setDishesError(result.message || 'Failed to load dishes');
      }
    } catch (err) {
      setDishes([]);
      setDishesError(err?.message || 'Failed to load dishes');
    } finally {
      setDishesLoading(false);
    }
  }, [form.site_id, selectedSite, sites]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  useEffect(() => {
    if (modalOpen && !editingRule) loadDishes();
  }, [modalOpen, editingRule, loadDishes]);

  const openAdd = () => {
    setEditingRule(null);
    setForm(emptyRule());
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      dish_id: rule.dish_id || '',
      site_id: rule.site_id ?? '*',
      base_price: rule.base_price != null ? String(rule.base_price) : '',
      min_floor_price: rule.min_floor_price != null ? String(rule.min_floor_price) : '',
      max_surge_price: rule.max_surge_price != null ? String(rule.max_surge_price) : '',
      spread_limit_percent: rule.spread_limit_percent != null ? String(rule.spread_limit_percent) : '',
      pricing_mode: rule.pricing_mode || 'auto',
      is_active: rule.is_active !== false,
      target_margin_percent: rule.target_margin_percent != null ? String(rule.target_margin_percent) : '',
      price_rounding_rule: rule.price_rounding_rule || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRule(null);
    setForm(emptyRule());
  };

  const handleSaveRule = async () => {
    const payload = {
      dish_id: form.dish_id,
      site_id: form.site_id || '*',
      base_price: form.base_price !== '' ? Number(form.base_price) : undefined,
      min_floor_price: form.min_floor_price !== '' ? Number(form.min_floor_price) : undefined,
      max_surge_price: form.max_surge_price !== '' ? Number(form.max_surge_price) : undefined,
      spread_limit_percent: form.spread_limit_percent !== '' ? Number(form.spread_limit_percent) : undefined,
      pricing_mode: form.pricing_mode,
      is_active: form.is_active,
      target_margin_percent: form.target_margin_percent !== '' ? Number(form.target_margin_percent) : undefined,
      price_rounding_rule: form.price_rounding_rule || undefined,
    };
    
    // Include signals in the payload if they have values (backend will update signals when provided)
    // Signals are site-level, so we need a valid site_id (not empty, not '*')
    // Use selectedSite if it's a valid site, otherwise try to extract from form or rule
    let signalsSiteId = null;
    if (selectedSite && selectedSite !== '' && selectedSite !== '*') {
      signalsSiteId = selectedSite;
    } else if (editingRule?.site_id && editingRule.site_id !== '*') {
      signalsSiteId = editingRule.site_id;
    } else if (form.site_id && form.site_id !== '*') {
      signalsSiteId = form.site_id;
    }
    
    const hasSignals = signals.competitor_price_index !== '' || signals.event_weather_index !== '';
    if (hasSignals) {
      if (signals.competitor_price_index !== '') {
        payload.competitor_price_index = Number(signals.competitor_price_index);
      }
      if (signals.event_weather_index !== '') {
        payload.event_weather_index = Number(signals.event_weather_index);
      }
      
      // Warn if no valid site_id for signals
      if (!signalsSiteId) {
        console.warn('⚠️ Signals provided but no valid site_id available. Signals will be included in rule update but may not be saved correctly.');
      }
    }
    
    // Validation: Min floor price cannot be > Base price
    if (payload.min_floor_price !== undefined && payload.base_price !== undefined) {
      if (payload.min_floor_price > payload.base_price) {
        alert('Min floor price cannot be greater than base price');
        return;
      }
    }

    // Validation: Spread limit percentage must be below 100
    if (payload.spread_limit_percent !== undefined) {
      if (payload.spread_limit_percent < 0 || payload.spread_limit_percent >= 100) {
        alert('Spread limit percentage must be between 0 and below 100');
        return;
      }
    }

    if (!payload.dish_id) return;
    setSaveLoading(true);
    try {
      if (editingRule) {
        // Use signalsSiteId for the site_id query parameter when signals are included
        // If no valid site_id for signals, use the rule's site_id
        const siteIdForRequest = hasSignals && signalsSiteId 
          ? signalsSiteId 
          : (editingRule.site_id ?? '*');
        
        // Log the request for debugging
        if (hasSignals) {
          console.log('📊 Updating pricing rule with signals:', {
            dish_id: editingRule.dish_id,
            site_id: siteIdForRequest,
            signalsSiteId,
            payload: {
              ...payload,
              competitor_price_index: payload.competitor_price_index,
              event_weather_index: payload.event_weather_index,
            }
          });
        }
        
        const result = await updatePricingRule(
          { dish_id: editingRule.dish_id, site_id: siteIdForRequest },
          payload
        );
        if (result.success) {
          // If signals were included and we have a valid site_id, also update signals separately
          // This ensures signals are updated even if the rule update endpoint doesn't handle them
          if (hasSignals && signalsSiteId) {
            try {
              console.log('📊 Also updating signals separately:', {
                site_id: signalsSiteId,
                competitor_price_index: payload.competitor_price_index,
                event_weather_index: payload.event_weather_index,
              });
              const signalsResult = await updatePricingSignals({
                site_id: signalsSiteId,
                competitor_price_index: payload.competitor_price_index,
                event_weather_index: payload.event_weather_index,
              });
              console.log('📊 Signals update result:', signalsResult);
            } catch (e) {
              console.error('❌ Failed to update signals after updating rule:', e);
              // Don't fail the rule update if signals update fails
            }
          } else if (hasSignals && !signalsSiteId) {
            console.warn('⚠️ Signals were provided but no valid site_id available. Signals may not be updated.');
          }
          closeModal();
          loadRules();
        } else {
          alert(result.message || 'Failed to update rule');
        }
        } else {
        // For create, also include signals if they have values
        // Note: createPricingRule doesn't accept site_id query param, but signals can still be included in body
        const result = await createPricingRule(payload);
        if (result.success) {
          // If signals were included and we have a valid site_id, also update signals separately
          // This ensures signals are updated even if create endpoint doesn't handle them
          if (hasSignals && signalsSiteId) {
            // Update signals separately to ensure they're saved
            try {
              await updatePricingSignals({
                site_id: signalsSiteId,
                competitor_price_index: payload.competitor_price_index,
                event_weather_index: payload.event_weather_index,
              });
            } catch (e) {
              console.warn('Failed to update signals after creating rule:', e);
              // Don't fail the rule creation if signals update fails
            }
          }
          closeModal();
          loadRules();
        } else {
          alert(result.message || 'Failed to create rule');
        }
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    const dishName = dishNames[rule.dish_id] || rule.dish_name || rule.dish_id;
    
    // Show delete confirmation overlay
    openOverlay('deleteDishPricingRuleWarning', {
      rule: rule,
      dishName: dishName,
      onConfirm: async (ruleToDelete) => {
        setDeleteLoadingId(ruleToDelete.dish_id);
        try {
          const result = await deletePricingRule({ dish_id: ruleToDelete.dish_id, site_id: ruleToDelete.site_id ?? '*' });
          if (result.success) {
            loadRules();
          } else {
            alert(result.message || 'Failed to delete rule');
          }
        } finally {
          setDeleteLoadingId(null);
        }
      }
    });
  };

  const handleSaveSignals = async () => {
    setSignalsSaving(true);
    setSignalsMessage(null);
    try {
      const result = await updatePricingSignals({
        site_id: selectedSite || undefined,
        competitor_price_index: signals.competitor_price_index === '' ? undefined : Number(signals.competitor_price_index),
        event_weather_index: signals.event_weather_index === '' ? undefined : Number(signals.event_weather_index),
      });
      if (result.success) {
        setSignalsMessage('Signals updated.');
        loadSignals();
      } else {
        setSignalsMessage(result.message || 'Failed to update signals');
      }
    } catch (e) {
      setSignalsMessage(e?.message || 'Failed to update signals');
    } finally {
      setSignalsSaving(false);
    }
  };

  const siteOptions = [{ site_id: '*', site_name: 'Select site' }, ...sites];

  const siteIdToName = useMemo(() => {
    const map = { '*': 'All outlets' };
    sites.forEach((s) => {
      const id = s.site_id || s.id;
      if (id) map[id] = s.site_name || s.name || id;
    });
    return map;
  }, [sites]);

  const dishIdToName = useMemo(() => {
    return dishNames;
  }, [dishNames]);

  const isFloorPriceInvalid = useMemo(() => {
    if (form.min_floor_price !== '' && form.base_price !== '') {
      return Number(form.min_floor_price) > Number(form.base_price);
    }
    return false;
  }, [form.min_floor_price, form.base_price]);

  const isBasePriceInvalid = useMemo(() => {
    if (form.base_price !== '') {
      return Number(form.base_price) <= 0;
    }
    return false;
  }, [form.base_price]);

  const isMaxSurgeInvalid = useMemo(() => {
    if (form.max_surge_price !== '' && form.base_price !== '') {
      return Number(form.max_surge_price) < Number(form.base_price);
    }
    return false;
  }, [form.max_surge_price, form.base_price]);

  const isSpreadLimitInvalid = useMemo(() => {
    if (form.spread_limit_percent !== '') {
      const val = Number(form.spread_limit_percent);
      return val < 0 || val >= 100;
    }
    return false;
  }, [form.spread_limit_percent]);

  const isTargetMarginInvalid = useMemo(() => {
    if (form.target_margin_percent !== '') {
      const val = Number(form.target_margin_percent);
      return val < 0 || val > 100;
    }
    return false;
  }, [form.target_margin_percent]);

  const isDuplicateRule = useMemo(() => {
    if (editingRule) return false;
    if (!form.dish_id) return false;
    const siteId = form.site_id || '*';
    return rules.some((r) => r.dish_id === form.dish_id && (r.site_id ?? '*') === siteId);
  }, [form.dish_id, form.site_id, editingRule, rules]);




  return (
    <div className="w-full h-full">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Pricing Rules</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Configure dish-level pricing rules: base price, floor/surge limits, and auto vs manual mode
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm min-w-[200px] text-gray-900"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.site_id || s.id} value={s.site_id || s.id}>
                {s.site_name || s.name || s.site_id}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={openAdd}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add rule
          </button>
        </div>
      </div>

      {/* Signals card */}
      <Card variant="solid" className="p-4 mb-6">
        <h3 className={`${typography.sh2} text-black mb-3 flex items-center gap-2`}>
          <Sliders className="w-4 h-4" />
          Signals (manual entry)
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Competitor price index and event/weather index used by the pricing engine. Optional; leave blank to keep current.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Competitor price index (0–100)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={signals.competitor_price_index}
              onChange={(e) => setSignals((s) => ({ ...s, competitor_price_index: e.target.value }))}
              disabled={signalsLoading}
              className="h-10 px-3 w-40 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event / weather index (0–100)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={signals.event_weather_index}
              onChange={(e) => setSignals((s) => ({ ...s, event_weather_index: e.target.value }))}
              disabled={signalsLoading}
              className="h-10 px-3 w-40 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleSaveSignals}
            disabled={signalsSaving || signalsLoading}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-60"
          >
            {signalsSaving ? 'Saving…' : 'Save signals'}
          </button>
          {signalsMessage && (
            <span className={`text-sm ${signalsMessage.startsWith('Signals') ? 'text-emerald-600' : 'text-red-600'}`}>
              {signalsMessage}
            </span>
          )}
        </div>
      </Card>

      {/* Rules table */}
      <Card variant="solid" className="p-4">
        <h3 className={`${typography.sh2} text-black mb-4`}>Dish pricing rules</h3>
        {rulesError && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            {rulesError}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="pb-2 pr-4 font-medium">Dish</th>
                <th className="pb-2 pr-4 font-medium">Site</th>
                <th className="pb-2 pr-4 font-medium">Base</th>
                <th className="pb-2 pr-4 font-medium">Floor</th>
                <th className="pb-2 pr-4 font-medium">Surge</th>
                <th className="pb-2 pr-4 font-medium">Spread %</th>
                <th className="pb-2 pr-4 font-medium">Mode</th>
                <th className="pb-2 pr-4 font-medium">Active</th>
                <th className="pb-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rulesLoading ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-400">
                    Loading rules…
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-400">
                    No pricing rules. Add a rule to get started.
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={`${rule.dish_id}-${rule.site_id ?? '*'}`} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-black">{dishIdToName[rule.dish_id] || rule.dish_name || rule.dish_id}</td>
                    <td className="py-2 pr-4 text-gray-600">{siteIdToName[rule.site_id ?? '*'] ?? rule.site_id}</td>
                    <td className="py-2 pr-4 text-gray-900">₹{rule.base_price}</td>
                    <td className="py-2 pr-4 text-gray-600">₹{rule.min_floor_price ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">₹{rule.max_surge_price ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{rule.spread_limit_percent != null ? `${rule.spread_limit_percent}%` : '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{rule.pricing_mode === 'auto' ? 'Auto' : 'Manual'}</td>
                    <td className="py-2 pr-4">
                      <span className={rule.is_active !== false ? 'text-emerald-600' : 'text-gray-400'}>
                        {rule.is_active !== false ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="py-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(rule)}
                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRule(rule)}
                        disabled={deleteLoadingId === rule.dish_id}
                        className="p-1.5 rounded text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit rule modal */}
      {modalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pricing-rule-modal-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 id="pricing-rule-modal-title" className={`${typography.h4} text-black`}>
                  {editingRule ? 'Edit pricing rule' : 'Add pricing rule'}
                </h2>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1 min-h-0 space-y-4">
                
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site <span className="text-red-500">*</span></label>
                  <select
                    value={form.site_id}
                    onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}
                    className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                  >
                    {siteOptions.map((s) => (
                      <option key={s.site_id} value={s.site_id}>
                        {s.site_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dish <span className="text-red-500">*</span></label>
                  <select
                    value={form.dish_id}
                    onChange={(e) => setForm((f) => ({ ...f, dish_id: e.target.value }))}
                    disabled={!!editingRule || dishesLoading}
                    className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                  >
                    <option value="">
                      {dishesLoading ? 'Loading dishes…' : dishesError ? 'Failed to load dishes' : 'Select dish'}
                    </option>
                    {dishes.map((d) => (
                      <option key={d.dish_id} value={d.dish_id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  {dishesError && (
                    <p className="mt-1 text-sm text-red-600">{dishesError}</p>
                  )}
                  {isDuplicateRule && (
                    <p className="mt-1 text-sm text-red-600">
                      A rule for this dish and site already exists.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base price (₹) <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={form.base_price}
                      onChange={(e) => setForm((f) => ({ ...f, base_price: e.target.value }))}
                      className={`w-full h-10 px-3 bg-white rounded-lg border ${isBasePriceInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'} text-gray-900 focus:ring-2 text-sm`}
                    />
                    {isBasePriceInvalid && (
                      <p className="mt-1 text-xs text-red-600">
                        Must be &gt; 0
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min floor (₹)</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={form.min_floor_price}
                      onChange={(e) => setForm((f) => ({ ...f, min_floor_price: e.target.value }))}
                      className={`w-full h-10 px-3 bg-white rounded-lg border ${isFloorPriceInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'} text-gray-900 focus:ring-2 text-sm`}
                    />
                    {isFloorPriceInvalid && (
                      <p className="mt-1 text-xs text-red-600">
                        Cannot be &gt; base price
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max surge (₹)</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={form.max_surge_price}
                      onChange={(e) => setForm((f) => ({ ...f, max_surge_price: e.target.value }))}
                      className={`w-full h-10 px-3 bg-white rounded-lg border ${isMaxSurgeInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'} text-gray-900 focus:ring-2 text-sm`}
                    />
                    {isMaxSurgeInvalid && (
                      <p className="mt-1 text-xs text-red-600">
                        Cannot be &lt; base price
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Spread limit %</label>
                    <input
                      type="number"
                      min={0}
                      max={99.99}
                      step={0.5}
                      value={form.spread_limit_percent}
                      onChange={(e) => setForm((f) => ({ ...f, spread_limit_percent: e.target.value }))}
                      className={`w-full h-10 px-3 bg-white rounded-lg border ${isSpreadLimitInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'} text-gray-900 focus:ring-2 text-sm`}
                    />
                    {isSpreadLimitInvalid && (
                      <p className="mt-1 text-xs text-red-600">
                        Must be below 100
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target margin % (optional)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={form.target_margin_percent}
                      onChange={(e) => setForm((f) => ({ ...f, target_margin_percent: e.target.value }))}
                      className={`w-full h-10 px-3 bg-white rounded-lg border ${isTargetMarginInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40' : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'} text-gray-900 focus:ring-2 text-sm`}
                    />
                    {isTargetMarginInvalid && (
                      <p className="mt-1 text-xs text-red-600">
                        Must be between 0-100
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pricing mode</label>
                    <select
                      value={form.pricing_mode}
                      onChange={(e) => setForm((f) => ({ ...f, pricing_mode: e.target.value }))}
                      className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                    >
                      {PRICING_MODES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price rounding</label>
                    <select
                      value={form.price_rounding_rule}
                      onChange={(e) => setForm((f) => ({ ...f, price_rounding_rule: e.target.value }))}
                      className="w-full h-10 px-3 bg-white rounded-lg border border-gray-300 text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                    >
                      {ROUNDING_RULES.map((o) => (
                        <option key={o.value || 'none'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div> */}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="rule-active"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="rule-active" className="text-sm text-gray-700">
                    Rule is active
                  </label>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeModal}
                  className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveRule}
                  disabled={saveLoading || !form.dish_id || form.base_price === '' || isFloorPriceInvalid || isBasePriceInvalid || isMaxSurgeInvalid || isSpreadLimitInvalid || isTargetMarginInvalid || isDuplicateRule}
                  className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-60"
                >
                  {saveLoading ? 'Saving…' : editingRule ? 'Update rule' : 'Create rule'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PricingRulesScreen;
